import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

const EMPTY_ARRAY = [];

/**
 * A person as shown in a list: display name and, for a People page with
 * "Link to Profile Page" on, its link. Author items are never linked.
 */
export const personEntry = (author, index, fullNames = false) => {
  const name = fullNames
    ? author.full_name || author.title || author.publication_name || ''
    : author.publication_name || author.full_name || author.title || '';
  const type = String(author['@type'] || '').toLowerCase();
  const isLinkable =
    type === 'people' &&
    (author.is_linkable != null ? Boolean(author.is_linkable) : true);
  return {
    key: author['@id'] || author.title || `author-${index}`,
    name,
    href: author['@id'] && isLinkable ? flattenToAppURL(author['@id']) : null,
  };
};

/**
 * Renders a list of authors/people as links or plain text.
 *
 * inline=false (default): one entry per line (div wrapper)
 * inline=true:            comma-separated on a single line
 *
 * Links only People with "Link to Profile Page" enabled (is_linkable).
 * Author items are never linked; use People for linkable profiles.
 * review_state is ignored. Display name: publication_name → full_name → title,
 * or with fullNames the full name first, for where a citation name reads oddly.
 */
const AuthorsLink = ({ authors, inline = false, fullNames = false }) => {
  const authorItems = authors || EMPTY_ARRAY;

  const normalizedAuthors = useMemo(
    () =>
      authorItems.map((author, index) => personEntry(author, index, fullNames)),
    [authorItems, fullNames],
  );

  if (normalizedAuthors.length === 0) return null;

  return (
    <>
      {normalizedAuthors.map((author, index) => {
        const content = author.href ? (
          <UniversalLink href={author.href}>{author.name}</UniversalLink>
        ) : (
          <span>{author.name}</span>
        );

        return inline ? (
          <React.Fragment key={author.key}>
            {index > 0 && ', '}
            {content}
          </React.Fragment>
        ) : (
          <div key={author.key}>{content}</div>
        );
      })}
    </>
  );
};

AuthorsLink.propTypes = {
  inline: PropTypes.bool,
  fullNames: PropTypes.bool,
  authors: PropTypes.arrayOf(
    PropTypes.shape({
      '@id': PropTypes.string,
      '@type': PropTypes.string,
      review_state: PropTypes.string,
      is_linkable: PropTypes.bool,
      publication_name: PropTypes.string,
      full_name: PropTypes.string,
      title: PropTypes.string,
    }),
  ),
};

export default memo(AuthorsLink);
